import { Injectable } from '@angular/core';
import {
  MetadataBearer as __MetadataBearer,
  IdentityProvider,
  InitializeMiddleware,
  TokenIdentity,
} from '@smithy/types';
import { ApiClient } from 'frontend.client.api';
import { environment } from '../../environments/environment';
import { AuthHelperService } from './auth-helper.service';
import { TokenService } from './token.service';

type test = Parameters<typeof ApiClient.prototype.send>[0];

export class JWTProvider {
  tokenService: TokenService;
  constructor(tk: TokenService) {
    this.tokenService = tk;
  }

  getIdentity = async (
    identityProperties?: Record<string, any>,
  ): Promise<TokenIdentity> => {
    const token = await this.tokenService.getToken();
    return {
      token: token ?? '',
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private apiClient: ApiClient | undefined;
  constructor(
    private tokenService: TokenService,
    private authHelperService: AuthHelperService,
  ) {
    const endpoint = environment.API_CLIENT_URL;
    const jwtProvider: IdentityProvider<TokenIdentity> = new JWTProvider(
      this.tokenService,
    ).getIdentity;
    this.apiClient = new ApiClient({
      endpoint,
      maxAttempts: 3,
      token: jwtProvider,
    });
    this.authHelperService.setApiClient(this.apiClient);
  }

  public get client() {
    return this.apiClient;
  }

  public addMiddleware<
    I extends object,
    IT extends I,
    OT extends CO,
    Q,
    CO extends __MetadataBearer = any,
  >(command: any) {
    const auth = this.getAuthMiddleware();
    const middleware: any = auth;
    command.middlewareStack.add(middleware, {
      step: 'build',
      name: 'authorizationMiddleware',
      tags: ['AUTH'],
    });
  }

  private getAuthMiddleware<IT extends object, OT extends object>() {
    const x: InitializeMiddleware<IT, OT> = (next, _) => async (args: any) => {
      const token = await this.tokenService.getToken();
      args.request.headers['Authorization'] = 'Bearer ' + token;
      const result = await next(args);
      return result;
    };
    return x;
  }
}
